import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "fileInput", "attachments"]
  static values = { token: String }
  boundHandleFileSelect = this.handleFileSelect.bind(this)

  connect() {
    // Create hidden file input
    this.fileInput = document.createElement("input")
    this.fileInput.type = "file"
    this.fileInput.className = "hidden"
    this.fileInput.addEventListener("change", this.boundHandleFileSelect)
    this.element.appendChild(this.fileInput)
  }

  disconnect() {
    this.fileInput.removeEventListener("change", this.boundHandleFileSelect)
    this.fileInput.remove()
  }

  selectFile(event) {
    event.preventDefault()
    if (!this.legalAcceptanceAccepted()) {
      alert("Please acknowledge the Terms of Service and Privacy Notice before uploading.")
      return
    }
    this.fileInput.click()
  }

  async handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    const maxSize = 25 * 1024 * 1024 // 25MB
    if (file.size > maxSize) {
      alert(`File "${file.name}" is too large. Maximum size is 25MB.`)
      return
    }

    // Show loading state
    this.setLoading(true)

    const formData = new FormData()
    formData.append("file", file)
    formData.append("task_token", this.tokenValue)
    if (this.legalAcceptanceAccepted()) {
      formData.append("legal_acceptance[accepted]", "1")
    }

    try {
      const response = await fetch("/paperwork_attachments/public_upload", {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (response.ok) {
        // Add the new attachment to the list
        this.addAttachmentBadge(data.filename)
        this.setLoading(false)
      } else {
        alert(data.error || "Upload failed")
        this.setLoading(false)
      }
    } catch (error) {
      alert("Upload failed: " + error.message)
      this.setLoading(false)
    }

    // Clear the file input
    this.fileInput.value = ""
  }

  addAttachmentBadge(filename) {
    if (this.hasAttachmentsTarget) {
      const badge = document.createElement("span")
      badge.className = "badge badge-ghost gap-1"
      badge.innerHTML = `<i class="ti ti-paperclip text-xs"></i><span class="truncate max-w-[150px]">${this.escapeHtml(filename)}</span>`
      this.attachmentsTarget.appendChild(badge)
      
      // Show the attachments container if it was hidden
      this.attachmentsTarget.closest('[data-attachments-container]')?.classList.remove('hidden')
    }
  }

  setLoading(loading) {
    const button = this.buttonTarget
    if (loading) {
      button.disabled = true
      button.dataset.originalHtml = button.innerHTML
      button.innerHTML = '<span class="loading loading-spinner loading-xs"></span>'
    } else {
      button.disabled = false
      button.innerHTML = button.dataset.originalHtml
    }
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  legalAcceptanceAccepted() {
    const checkbox = this.element.closest("form")?.querySelector('input[name="legal_acceptance[accepted]"]')
    return !checkbox || checkbox.checked
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
